/* eslint-disable no-nested-ternary */
import { useState } from 'react';
import {
  Box, Button, Divider, Typography, IconButton, Menu, MenuItem,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  setPlayerTeam, addPlayerToTeam, changePlayerType, checkOut, addPriority, removePriority,
  toggleStayIn,
} from 'redux/slices';
import SportsHandballIcon from '@mui/icons-material/SportsHandball';
import SportsVolleyballIcon from '@mui/icons-material/SportsVolleyball';
import FemaleIcon from '@mui/icons-material/Female';
import MoreVertIcon from '@mui/icons-material/MoreVert';

const formatWaitingFor = (waitingFor, checkedInNow = false) => {
  if (waitingFor < 0) return 'check-in na próxima';

  // Com prioridade, nunca mostrar números — apenas indicar a prioridade.
  if (waitingFor >= 10) return 'Próxima';

  // Acabou de sair da quadra ou acabou de fazer check-in.
  if (waitingFor === 0) return checkedInNow ? 'Check-in agora' : 'Saiu agora';

  // Valores fracionários (x.99) vêm do check-in na próxima: meio passo atrás.
  const whole = Math.floor(waitingFor);
  const hasHalf = !Number.isInteger(waitingFor);
  if (whole === 0) return 'Meia partida aguardando';
  return `${whole}ª partida${hasHalf ? ' e meia' : ''} aguardando`;
};

export default function WaitingPlayerCard({ player, isGuestList = false, fullWidth = false }) {
  const dispatch = useDispatch();
  const teams = useSelector((state) => state.players);
  const { playersPerTeam, showWinPercentage } = useSelector((state) => state.game);
  const { playerType } = player;
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCheckout = () => {
    dispatch(checkOut(player.playerId));
    handleMenuClose();
  };

  const hasPriority = player.waitingFor >= 10;

  const handlePrioritize = () => {
    if (hasPriority) {
      dispatch(removePriority(player.playerId));
    } else {
      dispatch(addPriority(player.playerId));
    }
    handleMenuClose();
  };

  const handleStayIn = () => {
    dispatch(toggleStayIn(player.playerId));
    handleMenuClose();
  };

  const dispatchPlayerType = (type) => {
    dispatch(changePlayerType({ playerId: player.playerId, playerType: type }));
  };

  const handleClick = () => {
    console.log('playerType', playerType);
    const isAttacker = playerType === 'Attacker';
    const isSetter = playerType === 'Setter';
    const isFlex = playerType === 'Flex';
    const isBoth = playerType === 'Both';
    if (isSetter) { return dispatchPlayerType('Attacker'); }
    if (isAttacker) { return dispatchPlayerType('Flex'); }
    if (isFlex) { return dispatchPlayerType('Both'); }
    if (isBoth) { return dispatchPlayerType('Setter'); }
    return dispatchPlayerType('Setter');
  };

  const isSetter = player.playerType === 'Setter';
  const isAttacker = player.playerType === 'Attacker';
  const isBoth = player.playerType === 'Both';

  const handleAddPlayer = () => {
    const isTeamAFull = teams.filter((p) => p.teamId === 1).length === playersPerTeam;
    const isTeamBFull = teams.filter((p) => p.teamId === 2).length === playersPerTeam;
    const teamId = isTeamAFull ? 2 : 1;

    if (!isTeamAFull || !isTeamBFull) {
      dispatch(setPlayerTeam({ playerId: player.playerId, teamId }));
      return dispatch(addPlayerToTeam({ playerId: player.playerId, teamId }));
    } return alert('Não há times disponíveis');
  };

  return (
    <Box sx={{
      width: fullWidth ? '100%' : { xs: '100%', md: '80%' },
    }}
    >
      <Box
        key={`${player.playerId}-${player.playerName}`}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          borderRadius: '5px',
          padding: '10px',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2,
          }}
        >
          <Box onClick={handleClick}>
            <Typography
              variant="h6"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <Typography
                component="span"
                variant="body2"
                sx={{ color: 'primary.main', fontWeight: 'bold', mr: 0.5 }}
              >
                {showWinPercentage
                  ? `${(player.dailyGames || 0) > 0 ? Math.round(((player.dailyWins || 0) / (player.dailyGames || 1)) * 100) : 0}%`
                  : `${player.dailyWins || 0}/${player.dailyGames || 0}`}
              </Typography>
              {player.playerName}
              {isAttacker && <SportsHandballIcon onClick={handleClick} sx={{ ml: 0.7, color: 'blue', height: '30px' }} /> }
              {isSetter && <SportsVolleyballIcon onClick={handleClick} sx={{ ml: 0.7, color: 'red', height: '30px' }} /> }
              {isBoth && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                }}
              >
                <SportsHandballIcon onClick={handleClick} sx={{ ml: 0.7, color: 'blue', height: '30px' }} />
                <SportsVolleyballIcon onClick={handleClick} sx={{ ml: 0.7, color: 'red', height: '30px' }} />
              </Box>
              )}
              {player.gender === 'F' && <FemaleIcon sx={{ ml: 0.7, color: 'deeppink', height: '30px' }} />}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', fontSize: '0.8rem' }}
            >
              {formatWaitingFor(player.waitingFor, player.checkedInNow)}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
          }}
        >
          {!isGuestList && (
          <Button
            variant="contained"
            onClick={handleAddPlayer}
          >
            Jogar
          </Button>
          )}
          <IconButton onClick={handleMenuClick} size="small">
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleCheckout}>Checkout</MenuItem>
            <MenuItem onClick={handlePrioritize}>
              {hasPriority ? 'Remover próxima' : 'Próxima'}
            </MenuItem>
            <MenuItem onClick={handleStayIn}>
              {player.stayIn ? 'Regular' : 'Stay In'}
            </MenuItem>
          </Menu>

        </Box>
      </Box>
      <Divider variant="fullWidth" />
    </Box>
  );
}
